// Deterministic briefing: arranges existing prepared summaries in basket
// order. It does not generate, rewrite, or synthesize any text.
import {
  CONTENT_TYPE_LABELS,
  LENS_LABELS,
  TOPIC_LABELS,
  type Lens,
  type Story,
} from "../../shared/model.ts";
import type { Basket } from "./basket.ts";
import { normalize } from "./basket.ts";
import { plainSummary } from "../../shared/summaryText.ts";

export interface BriefingSection {
  storyId: string;
  heading: string;
  meta: string;
  body: string;
  whyItMatters: string;
  sourceUrl: string;
  sourceName: string;
  isRead: boolean;
  isSample: boolean;
  unreviewed: boolean;
}

export interface Briefing {
  title: string;
  lensLabel: string;
  totalMinutes: number;
  sections: BriefingSection[];
  unavailableCount: number;
  containsSamples: boolean;
}

export function buildBriefing(basket: Basket, storiesById: Map<string, Story>, lens: Lens): Briefing {
  const sections: BriefingSection[] = [];
  let unavailableCount = 0;
  for (const item of normalize(basket)) {
    const s = storiesById.get(item.storyId);
    if (!s) {
      unavailableCount++;
      continue;
    }
    sections.push({
      storyId: s.id,
      heading: s.title,
      meta: [
        TOPIC_LABELS[s.primaryTopic],
        CONTENT_TYPE_LABELS[s.contentType],
        s.publishedAt ? formatDate(s.publishedAt) : "Date not provided",
        `~${s.estimatedReadMinutes} min (estimate)`,
      ].join(" · "),
      body: plainSummary(s.summaries[lens]),
      whyItMatters: s.whyItMatters,
      sourceUrl: s.canonicalUrl,
      sourceName: s.sourceName,
      isRead: item.isRead,
      isSample: s.isSample,
      unreviewed: s.summaryProvenance === "source_excerpt",
    });
  }
  return {
    title: "My Briefing",
    lensLabel: LENS_LABELS[lens],
    totalMinutes: sections.reduce((n, sec) => n + (storiesById.get(sec.storyId)?.estimatedReadMinutes ?? 0), 0),
    sections,
    unavailableCount,
    containsSamples: sections.some((s) => s.isSample),
  };
}

export function briefingToText(b: Briefing): string {
  const lines = [`${b.title} — ${b.lensLabel} lens`, ""];
  b.sections.forEach((s, i) => {
    lines.push(`${i + 1}. ${s.heading}${s.isSample ? " [SAMPLE — fictional]" : ""}`, s.meta, "", s.body, "", `Why it matters: ${s.whyItMatters}`, `Source: ${s.sourceUrl}`, "");
  });
  lines.push("Assembled from prepared Databricks Pulse summaries. Not an official Databricks statement.");
  return lines.join("\n");
}

export function formatDate(ms: number): string {
  return new Date(ms).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric", timeZone: "UTC" });
}
